import { useEffect, useRef, useState, useCallback, useSyncExternalStore } from 'react'
import { fetchHistoricalCandles } from '../services/candle-service'
import { calculateIntradayLevels, type IndicatorLevels } from '../lib/technical-indicators'

interface CachedIndicatorEntry {
    indicators: IndicatorLevels
    candles: any[]
    fetchedAt: number
}

// Global module-level store & subscriber registry to eliminate duplicate fetches across components
const INDICATOR_CACHE = new Map<string, CachedIndicatorEntry>()
const IN_FLIGHT_INDICATORS = new Map<string, Promise<CachedIndicatorEntry | null>>()
const SUBSCRIBERS = new Map<string, Set<() => void>>()

function getCacheKey(symbol: string, historicalDate?: string | null): string {
    return `${symbol || 'NIFTY'}:${historicalDate || 'live'}`
}

function notifySubscribers(cacheKey: string) {
    const subs = SUBSCRIBERS.get(cacheKey)
    if (subs) {
        subs.forEach((cb) => {
            try {
                cb()
            } catch (_) {}
        })
    }
}

async function fetchGlobalIndicators(
    symbol: string,
    historicalDate?: string | null,
): Promise<CachedIndicatorEntry | null> {
    if (!symbol) return null
    const cacheKey = getCacheKey(symbol, historicalDate)

    if (IN_FLIGHT_INDICATORS.has(cacheKey)) {
        return IN_FLIGHT_INDICATORS.get(cacheKey)!
    }

    const fetchPromise = (async () => {
        try {
            const results = await Promise.allSettled([
                fetchHistoricalCandles(symbol, 60, 'day', undefined, historicalDate),
                fetchHistoricalCandles(symbol, 180, 'month', undefined, historicalDate),
                fetchHistoricalCandles(symbol, 180, 'week', undefined, historicalDate),
                fetchHistoricalCandles(symbol, 10, '1minute', undefined, historicalDate),
            ])

            const dailyData = results[0].status === 'fulfilled' ? results[0].value : []
            const monthlyData = results[1].status === 'fulfilled' ? results[1].value : []
            const weeklyData = results[2].status === 'fulfilled' ? results[2].value : []
            const minuteData = results[3].status === 'fulfilled' ? results[3].value : []

            const referenceDate = historicalDate ? new Date(historicalDate) : new Date()
            const todayStr = referenceDate.toDateString()

            let prevDay = null
            if (dailyData.length > 0) {
                const lastDay = dailyData[dailyData.length - 1]
                if (lastDay) {
                    const isToday = new Date(lastDay.timestamp).toDateString() === todayStr
                    const prevDayRaw = isToday
                        ? dailyData.length > 1
                            ? dailyData[dailyData.length - 2]
                            : lastDay
                        : lastDay
                    if (prevDayRaw)
                        prevDay = { high: prevDayRaw.high, low: prevDayRaw.low, close: prevDayRaw.close }
                }
            }

            let prevMonth = null
            if (monthlyData.length > 0) {
                const lastMonth = monthlyData[monthlyData.length - 1]
                if (lastMonth) {
                    const isCurrentMonth =
                        new Date(lastMonth.timestamp).getMonth() === referenceDate.getMonth() &&
                        new Date(lastMonth.timestamp).getFullYear() === referenceDate.getFullYear()
                    const prevMonthRaw = isCurrentMonth
                        ? monthlyData.length > 1
                            ? monthlyData[monthlyData.length - 2]
                            : lastMonth
                        : lastMonth
                    if (prevMonthRaw)
                        prevMonth = {
                            high: prevMonthRaw.high,
                            low: prevMonthRaw.low,
                            close: prevMonthRaw.close,
                        }
                }
            } else if (weeklyData.length > 0) {
                const oldestWeek = weeklyData[0]
                if (oldestWeek)
                    prevMonth = { high: oldestWeek.high, low: oldestWeek.low, close: oldestWeek.close }
            } else if (dailyData.length > 0) {
                const oldestDay = dailyData[0]
                if (oldestDay) prevMonth = { high: oldestDay.high, low: oldestDay.low, close: oldestDay.close }
            }

            let prevWeek = null
            if (weeklyData.length > 0) {
                const lastWeek = weeklyData[weeklyData.length - 1]
                if (lastWeek) {
                    const daysDiff =
                        (referenceDate.getTime() - new Date(lastWeek.timestamp).getTime()) / (1000 * 3600 * 24)
                    const prevWeekRaw =
                        daysDiff < 7
                            ? weeklyData.length > 1
                                ? weeklyData[weeklyData.length - 2]
                                : lastWeek
                            : lastWeek
                    if (prevWeekRaw)
                        prevWeek = { high: prevWeekRaw.high, low: prevWeekRaw.low, close: prevWeekRaw.close }
                }
            } else if (dailyData.length > 0) {
                const oldestDay = dailyData[0]
                if (oldestDay) prevWeek = { high: oldestDay.high, low: oldestDay.low, close: oldestDay.close }
            }

            let prev2Months = null
            if (dailyData.length > 0) {
                const last60 = dailyData.slice(-60)
                if (last60.length > 0) {
                    const high = Math.max(...last60.map((d: any) => d.high))
                    const low = Math.min(...last60.map((d: any) => d.low))
                    const close = last60[last60.length - 1]?.close ?? 0
                    prev2Months = { high, low, close }
                }
            }

            const todayCandles = minuteData.filter(
                (c: any) => new Date(c.timestamp).toDateString() === todayStr,
            )
            const intradayLevels = calculateIntradayLevels(todayCandles)

            let todayOpen = 0
            let todayHigh = 0
            let todayLow = 0

            if (todayCandles.length > 0 && todayCandles[0]) {
                todayOpen = todayCandles[0].open || 0
                todayHigh = Math.max(...todayCandles.map((c: any) => c.high || 0))
                todayLow = Math.min(...todayCandles.map((c: any) => c.low || 0))
            } else if (dailyData.length > 0) {
                const dayCandle = dailyData.find((c: any) => new Date(c.timestamp).toDateString() === todayStr)
                if (dayCandle) {
                    todayOpen = dayCandle.open || 0
                    todayHigh = dayCandle.high || 0
                    todayLow = dayCandle.low || 0
                }
            }

            const indLevels: IndicatorLevels = {
                prev2Months,
                prevDay,
                prevWeek,
                prevMonth,
                first5Min: intradayLevels.first5Min,
                first15Min: intradayLevels.first15Min,
                todayOpen,
                todayHigh,
                todayLow,
            }

            const entry: CachedIndicatorEntry = {
                indicators: indLevels,
                candles: minuteData || [],
                fetchedAt: Date.now(),
            }

            INDICATOR_CACHE.set(cacheKey, entry)

            try {
                const { HistoryCache } = await import('../lib/idb-cache')
                await HistoryCache.set(`indicators_index_${symbol}`, [indLevels] as any, '')
            } catch (_) {}

            notifySubscribers(cacheKey)
            return entry
        } catch (e) {
            console.error('[useIndexIndicators] Failed to load index indicators:', e)
            return null
        } finally {
            IN_FLIGHT_INDICATORS.delete(cacheKey)
        }
    })()

    IN_FLIGHT_INDICATORS.set(cacheKey, fetchPromise)
    return fetchPromise
}

// Global single interval timer for the active symbol live polling
let globalPollingTimer: any = null
let currentPollingKey: string | null = null

function ensurePolling(symbol: string, historicalDate?: string | null) {
    if (historicalDate) {
        if (globalPollingTimer) {
            clearInterval(globalPollingTimer)
            globalPollingTimer = null
            currentPollingKey = null
        }
        return
    }

    const key = getCacheKey(symbol, historicalDate)
    if (currentPollingKey === key && globalPollingTimer) return

    currentPollingKey = key
    if (globalPollingTimer) clearInterval(globalPollingTimer)

    globalPollingTimer = setInterval(() => {
        const cached = INDICATOR_CACHE.get(key)
        // Only refresh if tab is visible and cache is older than 50s
        if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
            if (!cached || Date.now() - cached.fetchedAt >= 50000) {
                fetchGlobalIndicators(symbol, historicalDate)
            }
        }
    }, 60000)
}

export function useIndexIndicators(symbol: string, historicalDate?: string | null) {
    const cacheKey = getCacheKey(symbol, historicalDate)

    const subscribe = useCallback(
        (onStoreChange: () => void) => {
            let set = SUBSCRIBERS.get(cacheKey)
            if (!set) {
                set = new Set()
                SUBSCRIBERS.set(cacheKey, set)
            }
            set.add(onStoreChange)

            return () => {
                const s = SUBSCRIBERS.get(cacheKey)
                if (s) {
                    s.delete(onStoreChange)
                    if (s.size === 0) SUBSCRIBERS.delete(cacheKey)
                }
            }
        },
        [cacheKey],
    )

    const getSnapshot = useCallback(() => {
        return INDICATOR_CACHE.get(cacheKey)
    }, [cacheKey])

    const cachedData = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
    const [loading, setLoading] = useState(!cachedData)

    useEffect(() => {
        if (!symbol) return

        ensurePolling(symbol, historicalDate)

        const cached = INDICATOR_CACHE.get(cacheKey)
        const isFresh = cached && (historicalDate ? true : Date.now() - cached.fetchedAt < 60000)

        if (!isFresh) {
            setLoading(true)
            fetchGlobalIndicators(symbol, historicalDate).finally(() => {
                setLoading(false)
            })
        } else {
            setLoading(false)
        }
    }, [symbol, historicalDate, cacheKey])

    return {
        indexIndicators: cachedData?.indicators,
        indexCandles: cachedData?.candles || [],
        loading,
    }
}
